import * as routeNames from '@/router/routeNames';

import { RouterError } from '@/models/main';
import Vue from 'vue';
import VueRouter from 'vue-router';
import { getVersion } from '@/api';
import { isNumeric } from '@/utils/Common';
import store from '@/store';

Vue.use(VueRouter);

const router = new VueRouter({
  mode: 'history',
  base: '/',
  routes: [
    {
      path: '/login',
      name: routeNames.LOGIN,
      meta: {
        layout: 'initial',
        isPublic: true,
        title: 'Login form',
      },
      component: () => import('@/views/Login.vue'),
    },
    {
      name: routeNames.HOME,
      path: '/home',
      meta: { layout: 'default', title: 'Home page' },
      component: () => import('@/views/Homepage/HomePage.vue'),
    },
    {
      name: routeNames.JOBS,
      path: '/dash',
      meta: { title: 'Dashboard', layout: 'default' },
      component: () => import('@/views/Dashboard/Dashboard.vue'),
    },
    {
      name: routeNames.DEFAULT,
      path: '/',
      meta: { layout: 'default' },
      redirect: { path: '/home' },
    },
    {
      name: routeNames.JOBS_TV,
      path: '/dash-tv',
      meta: { title: 'Dashboard TV', layout: 'default' },
      component: () => import('@/views/Dashboard/Dashboard.vue'),
    },
    {
      name: routeNames.BFIX_DASH,
      path: '/dash-bfix',
      meta: { title: 'BFIX Dash', layout: 'default' },
      component: () => import('@/views/BfixDashboard/BfixDash.vue'),
    },
    {
      name: routeNames.AIR_ROAD_JOBS,
      path: '/airroad/jobs',
      meta: { title: 'Air Road jobs', layout: 'default' },
      component: () => import('@/views/AirRoad/AirRoadJobs.vue'),
      beforeEnter(to, from, next) {
        checkPermissions(to, next);
      },
    },
    {
      name: routeNames.AIR_ROAD_BOOKED,
      path: '/airroad/booked',
      meta: { title: 'Air Road booked', layout: 'default' },
      component: () => import('@/views/AirRoad/AirRoadBooked.vue'),
      beforeEnter(to, from, next) {
        checkPermissions(to, next);
      },
    },
    {
      name: routeNames.JOB_TICKETS,
      path: '/tickets',
      meta: { title: 'Job tickets', layout: 'default' },
      component: () => import('@/views/TicketsDashboard.vue'),
      beforeEnter(to, from, next) {
        checkPermissions(to, next);
      },
    },
    {
      name: routeNames.JOB_BOOKING,
      path: '/jobbooking',
      meta: { title: 'Job booking', layout: 'default' },
      component: () => import('@/views/JobBooking/JobBooking.vue'),
    },
    {
      name: routeNames.PARCEL_JOB_BOOKING,
      path: '/jobbooking/general',
      meta: { title: 'Parcel / Job booking', layout: 'default' },
      component: () => import('@/views/JobBooking/JobBookingGeneral.vue'),
    },
    {
      path: '',
      component: () => import('@/views/JobbookingView.vue'),
      children: [
        {
          name: routeNames.NEW_JOB_BOOKING,
          path: '/jobbooking/new',
          meta: { title: 'New / Job Booking', subtitle: 'Create a job', layout: 'default' },
          component: () => import('@/views/JobBooking/NewJobbookingView.vue'),
        },
        {
          name: routeNames.CLONE_JOB_BOOKING,
          path: '/jobbooking/clone',
          meta: { title: 'Clone / Job Booking', subtitle: 'Clone a job', layout: 'default' },
          component: () => import('@/views/JobBooking/CloneJobbookingView.vue'),
          props: route => {
            return {
              jobId: Number(route.query.job_id),
              jobDate: route.query.job_date,
            };
          },
        },
      ],
    },
    {
      name: routeNames.ADVANCED_JOB_BOOKING,
      path: '/jobbooking/advanced',
      meta: { title: 'Advanced / Job booking', layout: 'default' },
      component: () => import('@/views/JobBooking/JobBookingAdvanced/JobBookingAdvanced.vue'),
    },
    {
      name: routeNames.WAREHOUSE_SAME_WH_JB,
      path: '/jobbooking/warehouse',
      meta: { title: 'Warehouse to same warehouse / Job booking', layout: 'default', isSameWH: true },
      component: () => import('@/views/JobBooking/JobBookingAdvanced/JobBookingWarehouseToWarehouse.vue'),
    },
    {
      name: routeNames.WAREHOUSE_TO_WH_JB,
      path: '/jobbooking/warehouse-to-warehouse',
      meta: { title: 'Warehouse to warehouse / Job booking', layout: 'default', isSameWH: false },
      component: () => import('@/views/JobBooking/JobBookingAdvanced/JobBookingWarehouseToWarehouse.vue'),
    },
    {
      name: routeNames.SITE_INSPECTION_JOB_BOOKING,
      path: '/jobbooking/site-inspection',
      meta: { title: 'Site Inspection / Job booking', layout: 'default' },
      component: () => import('@/views/JobBooking/JobBookingSiteInspection/JobBookingSiteInspection.vue'),
    },
    {
      name: routeNames.BREAK_FIX_JOB_BOOKING,
      path: '/jobbooking/break-fix',
      meta: { title: 'Break Fix / Job booking', layout: 'default' },
      component: () => import('@/views/JobBooking/JobBookingBreakFix/JobBookingBreakFix.vue'),
    },
    {
      name: routeNames.DRAFT_ADVANCED_JOB_BOOKING,
      path: '/jobbooking/advanced/draft/:id',
      meta: { title: 'Draft / Advanced / Job booking', layout: 'default' },
      component: () => import('@/views/JobBooking/JobBookingAdvanced/JobBookingAdvanced.vue'),
    },
    {
      name: routeNames.SPREADSHEET_JOB_BOOKING,
      path: '/jobbooking/spreadsheet',
      meta: { title: 'Spreadsheet / Job booking', layout: 'default' },
      component: () => import('@/views/JobBooking/SpreadSheet/SpreadSheet.vue'),
    },
    {
      name: routeNames.SPREADSHEET_B2B_BOOKING,
      path: '/jobbooking/b2b-spreadsheet',
      meta: { title: 'Spreadsheet / B2B booking', layout: 'default' },
      component: () => import('@/views/JobBooking/SpreadSheet/SpreadSheetB2B.vue'),
    },
    {
      name: routeNames.SEARCH,
      path: '/search',
      meta: { title: 'Search', layout: 'default' },
      component: () => import('@/views/SearchPage/SearchPage.vue'),
    },
    {
      name: routeNames.SEARCH_POSTCODE,
      path: '/search-postcode',
      meta: { title: 'Search Postcode', layout: 'default' },
      component: () => import('@/views/SearchPostcode/SearchPostcode.vue'),
      beforeEnter(to, from, next) {
        checkPermissions(to, next);
      },
    },
    {
      name: routeNames.DRAFT_JOB_BOOKING,
      path: '/jobbooking/drafts',
      meta: { title: 'Draft / Job booking', layout: 'default' },
      component: () => import('@/views/JobBooking/Drafts/Drafts.vue'),
    },
    {
      name: routeNames.TECHNICIANS,
      path: '/technicians',
      meta: { title: 'Technicians', layout: 'default' },
      component: () => import('@/views/Technicians/Technicians.vue'),
    },
    {
      name: routeNames.TECHNICIANS_CATALOG,
      path: '/technicians/catalog',
      meta: { title: 'Technicians catalog', layout: 'default' },
      component: () => import('@/views/Technicians/TechniciansCatalog.vue'),
    },
    {
      name: routeNames.WAREHOUSE_QUANT_PAGE,
      path: '/jobbooking/quants',
      meta: { title: 'Warehouse / Quant page', layout: 'default' },
      component: () => import('@/views/Warehouse/QuantPage.vue'),
    },
    // Detailed job by rh_id
    {
      name: routeNames.DETAILED_PARAM_JOB,
      path: '/job/:id',
      meta: { title: 'Job Detailed', layout: 'default' },
      component: () => import('@/views/JobDetailed/JobDetailed.vue'),
    },
    {
      name: routeNames.DETAILED_QUERY_JOB,
      path: '/detailed-job',
      // TODO: Should we make a unique title?
      meta: { title: 'Job Detailed', layout: 'default' },
      component: () => import('@/views/JobDetailed/JobDetailed.vue'),
      props: route => {
        if (route.query.job_pattern && typeof route.query.job_pattern === 'string') {
          return {
            // 123456-2022-01-01
            // TODO: Maybe make a redirect to job_id+job_date path?
            jobId: route.query.job_pattern.split('-')[0],
            jobDate: route.query.job_pattern.split('-').slice(-3).join('-'),
          };
        } else {
          return {
            jobId: JSON.parse(route.query.job_id as string),
            jobDate: route.query.job_date,
          };
        }
      },
    },
    {
      name: routeNames.TICKET_FROM_THE_SERVICE_DESK,
      path: '/job/external/:id',
      meta: { title: 'Service desk ticket from', layout: 'default' },
      redirect: to => {
        const ids = to.path.split('/job/external/')[1];
        // Multiple IDs
        if (ids.indexOf(',') >= 0) {
          // Redirect to the Error page if some word isn't a number
          const hasInvalidNumber = ids.split(',').some(id => {
            if (!isNumeric(id)) {
              return true;
            }
            return false;
          });
          if (hasInvalidNumber) {
            return { path: '/error' };
          }
          // Redirect to the Search page
          return { path: '/search', query: { job_id: ids } };
        }
        // Single ID
        return { path: `/job/${ids}` };
      },
    },
    {
      name: routeNames.SERVICE_DESK_TICKET,
      path: '/job/ticket/:id',
      meta: { title: 'Service desk ticket', layout: 'default' },
      component: () => import('@/views/JobDetailed/ZammadTicketPage.vue'),
    },
    {
      name: routeNames.LIST_QUOTES,
      path: '/quotes/list',
      meta: { title: 'My Quotes', layout: 'default' },
      component: () => import('@/views/QuoteList/QuoteList.vue'),
    },
    {
      name: routeNames.NEW_QUOTE,
      path: '/quotes/new',
      meta: { title: 'Quote Calculator', layout: 'default' },
      component: () => import('@/views/QuoteCalculator/QuoteCalculator.vue'),
    },
    {
      name: routeNames.DETAILED_QUOTE,
      path: '/quotes/:id',
      meta: { title: 'Quote Detail', layout: 'default' },
      component: () => import('@/views/QuoteDetail/QuoteDetail.vue'),
    },
    {
      name: routeNames.PERMANENT_JOBS_LIST,
      path: '/permanent',
      meta: { title: 'Permanent Jobs', layout: 'default' },
      component: () => import('@/views/PermanentJobs/PermanentJobsList.vue'),
      beforeEnter(to, from, next) {
        checkPermissions(to, next);
      },
    },
    {
      name: routeNames.PERMANENT_JOB_BOOKING,
      path: '/permanent/new',
      meta: { title: 'New Permanent Job', layout: 'default' },
      component: () => import('@/views/PermanentJobs/PermanentJobBooking.vue'),
      beforeEnter(to, from, next) {
        checkPermissions(to, next);
      },
    },
    {
      name: routeNames.PERMANENT_JOB_DETAIL,
      path: '/permanent/:id',
      meta: { title: 'Permanent Job Detail', layout: 'default' },
      component: () => import('@/views/PermanentJobs/PermanentJobDetail.vue'),
      beforeEnter(to, from, next) {
        checkPermissions(to, next);
      },
    },
    {
      name: routeNames.SCHEDULER_BOARD,
      path: '/scheduler/board/:division/:date',
      meta: { title: 'Scheduler board', layout: 'default' },
      component: () => import('@/views/Scheduler/Board/SchedulerBoard.vue'),
      beforeEnter(to, from, next) {
        checkPermissions(to, next);
      },
    },
    {
      name: routeNames.SCHEDULER_BOARD_LOGS,
      path: '/scheduler/board/:division/:date/logs',
      meta: { title: 'Scheduler board logs', layout: 'default' },
      component: () => import('@/views/Scheduler/Logs/SchedulerBoardLogs.vue'),
      beforeEnter(to, from, next) {
        checkPermissions(to, next);
      },
    },
    {
      name: routeNames.SCHEDULER,
      path: '/scheduler/:date?',
      meta: { title: 'Scheduler', layout: 'default' },
      component: () => import('@/views/Scheduler/Cards/SchedulerCards.vue'),
      beforeEnter(to, from, next) {
        checkPermissions(to, next);
      },
    },
    {
      name: routeNames.SCHEDULER_SHIFTS,
      path: '/scheduler/shifts/:date?',
      meta: { title: 'Scheduler Shifts', layout: 'default' },
      component: () => import('@/views/Scheduler/Shifts/Shifts.vue'),
      beforeEnter(to, from, next) {
        checkPermissions(to, next);
      },
    },
    {
      name: routeNames.PROFILE,
      path: '/profile',
      meta: { title: 'Profile', layout: 'default' },
      component: () => import('@/views/Profile/Profile.vue'),
    },
    {
      name: routeNames.ADDRESS_BOOK,
      path: '/user/address-book',
      meta: { title: 'Address Book', layout: 'default' },
      component: () => import('@/views/User/AddressBook.vue'),
    },
    {
      name: routeNames.REPORTS,
      path: '/reports/reports',
      meta: { title: 'Reports', layout: 'default' },
      component: () => import('@/views/Reports/BulkReports/BulkReports.vue'),
      beforeEnter(to, from, next) {
        checkPermissions(to, next);
      },
    },
    {
      name: routeNames.RUN_SHEET,
      path: '/reports/runsheet',
      meta: { title: 'Run Sheet', layout: 'default' },
      component: () => import('@/views/Reports/RunSheet/RunSheet.vue'),
      beforeEnter(to, from, next) {
        checkPermissions(to, next);
      },
    },
    {
      name: routeNames.KPI_REPORT,
      path: '/reports/kpi',
      meta: { title: 'KPI Report', layout: 'default' },
      component: () => import('@/views/Reports/KPI/KPI.vue'),
      beforeEnter(to, from, next) {
        checkPermissions(to, next);
      },
    },
    {
      name: routeNames.DRIVER_REPORT,
      path: '/reports/driver',
      meta: { title: 'Driver Report', layout: 'default' },
      component: () => import('@/views/Reports/Driver/Driver.vue'),
      beforeEnter(to, from, next) {
        checkPermissions(to, next);
      },
    },
    {
      name: routeNames.TOMORROW_REPORT,
      path: '/reports/tomorrow',
      meta: { title: 'Tomorrow Report', layout: 'default' },
      component: () => import('@/views/Reports/Tomorrow/Tomorrow.vue'),
      beforeEnter(to, from, next) {
        checkPermissions(to, next);
      },
    },
    {
      name: routeNames.WEEKLY_STATS_REPORT,
      path: '/reports/weekly-stats',
      meta: { title: 'Weekly Stats Report', layout: 'default' },
      component: () => import('@/views/Reports/Stats/Stats.vue'),
      beforeEnter(to, from, next) {
        checkPermissions(to, next);
      },
    },
    {
      name: routeNames.AM_PM_JOBS,
      path: '/reports/am-pm-job',
      meta: { title: 'AM/PM Jobs', layout: 'default' },
      component: () => import('@/views/Reports/DailyJobs/DailyJobs.vue'),
      beforeEnter(to, from, next) {
        checkPermissions(to, next);
      },
    },
    {
      name: routeNames.ETA_REPORT,
      path: '/reports/eta',
      meta: { title: 'ETA Report', layout: 'default' },
      component: () => import('@/views/Reports/ETAReport/ETA.vue'),
    },
    {
      name: routeNames.TRANSFERS,
      path: '/reports/transfers',
      meta: { title: 'Transfers Report', layout: 'default' },
      component: () => import('@/views/Reports/Transfers/Transfers.vue'),
    },
    {
      name: routeNames.UNINVOICED_JOBS,
      path: '/reports/uninvoiced-jobs',
      meta: { title: 'Uninvoiced Jobs', layout: 'default' },
      component: () => import('@/views/Reports/UninvoicedJobs/UninvoicedJobs.vue'),
      beforeEnter(to, from, next) {
        checkPermissions(to, next);
      },
    },
    {
      name: routeNames.OUTGOING_MACHINES,
      path: '/reports/outgoing-machines',
      meta: { title: 'Outgoing Machines', layout: 'default' },
      component: () => import('@/views/Reports/OutgoingMachines/OutgoingMachines.vue'),
    },
    {
      name: routeNames.BOOKED_JOBS,
      path: '/jobbooking/booked',
      meta: { title: 'Booked Jobs', layout: 'default' },
      component: () => import('@/views/JobsBooked/JobsBooked.vue'),
    },
    {
      name: routeNames.SUBURBS,
      path: '/suburbs',
      meta: { title: 'Suburbs', layout: 'default' },
      component: () => import('@/views/Suburbs/Suburbs.vue'),
    },
    {
      name: routeNames.MAILBOX_TEST,
      path: '/test/mailbox',
      meta: { title: 'Mailbox Test', layout: 'default' },
      component: () => import('@/views/Test/Mailbox.vue'),
      beforeEnter(to, from, next) {
        checkPermissions(to, next);
      },
    },
    {
      name: routeNames.LOCATIONS,
      path: '/locations',
      meta: { title: 'Locations', layout: 'default' },
      component: () => import('@/views/Location/Location.vue'),
      beforeEnter(to, from, next) {
        checkPermissions(to, next);
      },
    },
    {
      name: routeNames.NEXT_DAY_STOPS,
      path: '/next-day-stops',
      meta: { title: 'Next Day Stops', layout: 'default' },
      component: () => import('@/views/Location/NextDayMap.vue'),
      beforeEnter(to, from, next) {
        checkPermissions(to, next);
      },
    },
    {
      name: routeNames.DRIVER_RETRANSMIT,
      path: '/driver-retransmit',
      meta: { title: 'Driver Retransmit', layout: 'default' },
      component: () => import('@/views/DriverRetransmit/DriverRetransmit.vue'),
      beforeEnter(to, from, next) {
        checkPermissions(to, next);
      },
    },
    {
      name: routeNames.JOBHISTORY,
      path: '/job-history',
      meta: { title: 'Job History', layout: 'default' },
      component: () => import('@/views/JobDetailed/JobHistory.vue'),
      beforeEnter(to, from, next) {
        checkPermissions(to, next);
      },
    },
    {
      name: routeNames.NEWSLETTER,
      path: '/newsletter/:id',
      meta: { title: 'Newsletter', layout: 'default' },
      component: () => import('@/views/News/NewsEntryDetail.vue'),
      beforeEnter(to, from, next) {
        checkPermissions(to, next);
      },
    },
    {
      name: routeNames.QUANTS_CART_PAGE,
      path: '/cart',
      meta: { title: 'Quants Cart', layout: 'default' },
      component: () => import('@/views/JobBooking/Quants/QuantsCartPage.vue'),
      beforeEnter(to, from, next) {
        checkPermissions(to, next);
      },
    },
    {
      name: routeNames.INITIAL_ERROR,
      path: '/error',
      meta: { title: 'Error', layout: 'initial' },
      component: () => import('@/views/Errors.vue'),
    },
    {
      name: routeNames.PAGE_ERROR,
      path: '*',
      redirect: { path: '/error' },
    },
  ],
});

const notStaffRoutes = [
  routeNames.JOBS_TV,
  routeNames.BFIX_DASH,
  routeNames.AIR_ROAD_JOBS,
  routeNames.AIR_ROAD_BOOKED,
  routeNames.BOOKING_COMPLIANCE,
  routeNames.SCHEDULER_BOARD,
  routeNames.SCHEDULER_BOARD_LOGS,
  routeNames.SCHEDULER,
  routeNames.SCHEDULER_SHIFTS,
  routeNames.REPORTS,
  routeNames.RUN_SHEET,
  routeNames.KPI_REPORT,
  routeNames.DRIVER_REPORT,
  routeNames.TOMORROW_REPORT,
  routeNames.WEEKLY_STATS_REPORT,
  routeNames.AM_PM_JOBS,
  routeNames.LOCATIONS,
  routeNames.JOBHISTORY,
  routeNames.DRIVER_RETRANSMIT,
  routeNames.NEWSLETTER,
  routeNames.UNINVOICED_JOBS,
  routeNames.PERMANENT_JOBS_LIST,
  routeNames.PERMANENT_JOB_BOOKING,
  routeNames.PERMANENT_JOB_DETAIL,
];

const checkPermissions = (to, next) => {
  const permissions = store.getters['moduleAuthData/authData'].permissions;
  let stuff = false;
  if (permissions) {
    permissions.map(permission => {
      // looking for Job/staff
      if (permission.subject === 'Job' && permission.actions.indexOf('staff') >= 0) {
        stuff = true;
      }
    });
  }
  // redirecting user if the route is the list & user is not staff
  if (!stuff && to.name && notStaffRoutes.indexOf(to.name) !== -1) {
    next({
      path: '/',
    });
    return;
  }
  next();
};

/**
 * Before each page we check auth status from store and for private pages.
 */
router.beforeEach((to, from, next) => {
  store.dispatch('setGlobalLoader', true);
  if (to.matched.some(record => !record.meta.isPublic)) {
    if (!store.getters['moduleAuthData/isLoggedIn'] && to.name !== routeNames.LOGIN) {
      next({
        path: '/login',
      });
    } else {
      // If we are going anywhere from Login page where we have been push from previous page
      // we redirect to that previous page
      if (store.getters.prevUrl.url !== '' && from.name === routeNames.LOGIN) {
        const prevPath = store.getters.prevUrl.url;
        store.dispatch('setRouterData', {
          name: '',
          url: '',
        });
        next({
          path: prevPath,
        });
      } else {
        next();
      }
    }
  } else {
    next();
  }
  // Write down "TO" route in Store to be able to redirect there after successful LogIn
  // in the case when use has been pushed to "login" from somewhere
  if (to.name !== routeNames.LOGIN) {
    store.dispatch('setRouterData', {
      name: to.name,
      url: to.fullPath,
    });
  }
});

router.beforeResolve((to, from, next) => {
  store.dispatch('setGlobalLoader', false);
  next();
});

// eslint-disable-next-line no-unused-vars,@typescript-eslint/no-unused-vars
router.afterEach((to, from) => {
  Vue.nextTick(() => {
    const title = to.meta?.title || '';
    document.title = 'KK ' + title;
  });
});

/**
 * Tracking the errors of loading chunks in order to reload
 * the current portal page if the version has changed
 */
router.onError(async (error: RouterError) => {
  if (error.name === 'ChunkLoadError' || error.code === 'CSS_CHUNK_LOAD_FAILED') {
    const actualVersion = await getVersion();
    if (actualVersion !== process.env.VERSION) {
      window.location.reload();
    }
  }
});

export default router;
